import React, { useState, useEffect, useRef } from "react";
import { Typography, Button, Box, Stack } from "@mui/material";
import { makeStyles } from "@mui/styles";
import AddIcon from "@mui/icons-material/Add";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import { useSelector, useDispatch } from "react-redux";
import { api } from "common";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FONT_FAMILY, MAIN_COLOR } from "../common/sharedFunctions";
import wavePattern from "../../../web-app/src/assets/img/wave.svg";
import AlertDialog from "../components/AlertDialog";
import AlertDialogTwoButton from "../components/AlertDialogTwoButton";
import TransactionTable from "./TransactionTable";
import AddToWallet from "components/WalletModals/AddToWallet";
import { ArrowForward, TrendingUp } from "@mui/icons-material";

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    padding: theme.spacing(1),
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    width: 540,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: 0,
    borderRadius: 12,
    overflow: "hidden",
    marginTop: 60,
  },
  modalContent: {
    padding: theme.spacing(3),
  },
  modalHeader: {
    fontSize: 32,
    fontWeight: "600",
    marginBottom: theme.spacing(3),
    fontFamily: FONT_FAMILY,
  },
  modalSubheader: {
    fontSize: 16,
    fontWeight: "400",
    color: "#21272A",
    marginBottom: theme.spacing(1),
    fontFamily: FONT_FAMILY,
  },
  inputRtl: {
    "& label": {
      right: 10,
      left: "auto",
      paddingRight: 20,
    },
    "& legend": {
      textAlign: "right",
      marginRight: 20,
    },
  },
  textField: {
    "& label.Mui-focused": {
      color: MAIN_COLOR,
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: MAIN_COLOR,
    },
    "& .MuiFilledInput-underline:after": {
      borderBottomColor: MAIN_COLOR,
    },
    "& .MuiOutlinedInput-root": {
      "&.Mui-focused fieldset": {
        borderColor: MAIN_COLOR,
      },
    },
  },
  amountInput: {
    fontSize: 16,
    marginBottom: 8,
    padding: "15px 14px",
    borderRadius: 4,
    border: "none",
    backgroundColor: "#f5f5f5",
    width: "100%",
    boxSizing: "border-box",
    fontFamily: FONT_FAMILY,
    "&:focus": {
      outline: "none",
      border: `1px solid ${MAIN_COLOR}`,
    },
  },
  selectField: {
    color: "black",
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: MAIN_COLOR,
    },
    backgroundColor: "#f5f5f5",
    borderRadius: 4,
    "& .MuiSelect-select": {
      padding: "15px 14px",
    },
  },
  walletCard: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "20px 24px",
    minWidth: "280px",
    boxShadow: "none",
    border: "1px solid #E0E0E0",
    position: "relative",
    overflow: "hidden",
    height: "180px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  waveBg: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundImage: `url(${wavePattern})`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right",
    backgroundSize: "cover",
    opacity: 1.5,
    zIndex: 0,
  },
  walletBalanceLabel: {
    color: "#202224",
    fontWeight: 400,
    marginBottom: "8px",
    fontSize: "16px",
    fontFamily: "Source Sans Pro, sans-serif",
    zIndex: 1,
    position: "relative",
  },
  walletBalanceValue: {
    fontWeight: "bold",
    color: "#000",
    zIndex: 1,
    position: "relative",
    fontSize: "32px",
    fontFamily: "Source Sans Pro, sans-serif",
    lineHeight: "1.1",
    marginBottom: "0",
  },
  trendIndicator: {
    // color: "#D32F2F",
    display: "flex",
    alignItems: "center",
    fontWeight: 400,
    fontFamily: "Roboto",
    zIndex: 1,
    position: "relative",
    fontSize: "14px",
    marginTop: "auto",
    paddingTop: "20px",
  },
  trendIcon: {
    fontSize: "18px",
    marginRight: "4px",
  },
  addWalletButton: {
    backgroundColor: "#5BAA59",
    color: "white",
    borderRadius: "24px",
    padding: "12px 24px",
    textTransform: "none",
    fontSize: "16px",
    fontWeight: "600",
    boxShadow: "none",
    height: "48px",
    marginTop: "2px",
    "&:hover": {
      backgroundColor: "#6BB566",
    },
  },
  pageContainer: {
    padding: "24px 32px",
    maxWidth: "1200px",
    margin: "0 auto",
    backgroundColor: "#F8F9FA",
  },
  pageTitle: {
    marginBottom: "24px",
    color: "#263238",
    fontWeight: 600,
    fontSize: "28px",
  },
  transactionContainer: {
    marginTop: "40px",
  },
  modalButton: {
    borderRadius: "20px",
    paddingInline: "24px",
    textTransform: "none",
    fontWeight: 600,
    fontSize: "16px",
  },
  cancelButton: {
    backgroundColor: "#FFF",
    color: "#B3261E",
    border: "1px solid #B3261E",
    transition: "all 300ms ease",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#1F0318",
      color: "#FFFFFF",
      border: "none",
    },
  },
  actionButton: {
    backgroundColor: "#5BAA59",
    color: "white",
    "&:hover": {
      backgroundColor: "#F8DBF2",
    },
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: "rgba(0, 0, 0, 0.54)",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    marginTop: theme.spacing(3),
  },
  formLabel: {
    fontSize: 16,
    fontWeight: "400",
    color: "#21272A",
    marginBottom: theme.spacing(1),
    fontFamily: FONT_FAMILY,
  },
}));
const getStyles = (role) => ({
  addToWallet: {
    backgroundColor: role === "customer" ? "#5BAA59" : "#4F913C",
    color: "white",
    borderRadius: "30px",
    padding: "12px 24px",
    fontSize: "16px",
    fontWeight: "700",
    boxShadow: "none",
    height: "48px",
    width: "100%",
    whiteSpace: "nowrap",
    textTransform: "capitalize",
    transition: "all 0.3s ease-in-out",
    "&:hover": {
      backgroundColor: role === "customer" ? "#B3DBA8" : "#B3DBA8",
    },
  },
  withdrawFunds: {
    backgroundColor: "transparent",
    color: role === "customer" ? "#5BAA59" : "#4F913C",
    border: role === "customer" ? "2px solid #5BAA59" : "2px solid #4F913C",
    whiteSpace: "nowrap",
    textTransform: "capitalize",
    borderRadius: "30px",
    padding: "12px 24px",
    fontSize: "16px",
    fontWeight: "700",
    boxShadow: "none",
    height: "48px",
    width: "100%",
    transition: "all 0.3s ease-in-out",
    "&:hover": {
      borderColor: role === "customer" ? "#F8DBF2" : "#B3DBA8",
      backgroundColor: "transparent",
    },
  },
  cancelButton: {},
  actionButton: {
    backgroundColor: role === "customer" ? "#5BAA59" : "#4F913C",
    color: "white",
    "&:hover": {
      backgroundColor: role === "customer" ? "#F8DBF2" : "#B3DBA8",
    },
  },
});

const icons = {
  paypal: require("../assets/payment-icons/paypal-logo.png").default,
  braintree: require("../assets/payment-icons/braintree-logo.png").default,
  stripe: require("../assets/payment-icons/stripe-logo.png").default,
  paytm: require("../assets/payment-icons/paytm-logo.png").default,
  payulatam: require("../assets/payment-icons/payulatam-logo.png").default,
  flutterwave: require("../assets/payment-icons/flutterwave-logo.png").default,
  paystack: require("../assets/payment-icons/paystack-logo.png").default,
  securepay: require("../assets/payment-icons/securepay-logo.png").default,
  payfast: require("../assets/payment-icons/payfast-logo.png").default,
  liqpay: require("../assets/payment-icons/liqpay-logo.png").default,
  culqi: require("../assets/payment-icons/culqi-logo.png").default,
  mercadopago: require("../assets/payment-icons/mercadopago-logo.png").default,
  squareup: require("../assets/payment-icons/squareup-logo.png").default,
  wipay: require("../assets/payment-icons/wipay-logo.png").default,
  test: require("../assets/payment-icons/test-logo.png").default,
  razorpay: require("../assets/payment-icons/razorpay-logo.png").default,
  paymongo: require("../assets/payment-icons/paymongo-logo.png").default,
  iyzico: require("../assets/payment-icons/iyzico-logo.png").default,
  slickpay: require("../assets/payment-icons/slickpay-logo.png").default,
  xendit: require("../assets/payment-icons/xendit-logo.png").default,
  tap: require("../assets/payment-icons/tap-logo.png").default,
};

const UserWallet = () => {
  const { withdrawBalance } = api;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const auth = useSelector((state) => state.auth);
  const settings = useSelector((state) => state.settingsdata.settings);
  const providers = useSelector((state) => state.paymentmethods.providers);
  const role = useSelector((state) => state.auth.profile.usertype);
  const [profile, setProfile] = useState();
  const [data, setData] = useState([]);
  const [commonAlert, setCommonAlert] = useState({ open: false, msg: "" });
  const [commonAlertTwoButton, setCommonAlertTwoButton] = useState({
    open: false,
    msg: "",
  });
  const rootRef = useRef(null);
  const [amount, setAmount] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState("add"); // 'add' or 'withdraw'
  const [selectedProvider, setSelectedProvider] = useState();
  const [selectedProviderIndex, setSelectedProviderIndex] = useState(0);

  useEffect(() => {
    if (providers) {
      setSelectedProvider(providers[0]);
    }
  }, [providers]);

  useEffect(() => {
    if (auth.profile) {
      setProfile(auth.profile);
      let wdata = auth.walletHistory;
      var wallHis = [];
      for (let key in wdata) {
        wdata[key].walletKey = key;
        if (
          wdata[key].type.includes("Credit") ||
          wdata[key].type.includes("credit")
        ) {
          wdata[key].type = "credited";
        } else if (
          wdata[key].type.includes("Withdraw") ||
          wdata[key].type.includes("withdraw")
        ) {
          wdata[key].type = "withdraw";
        } else {
          wdata[key].type = "debited";
        }
        wallHis.push(wdata[key]);
      }
      if (wallHis.length > 0) {
        setData(wallHis.reverse());
      } else {
        setData([]);
      }
    } else {
      setProfile(null);
    }
  }, [auth.profile, auth.walletHistory]);

  const handleOpenModal = (type) => {
    if (
      !(
        profile?.mobile &&
        profile.mobile.length > 6 &&
        profile.email &&
        profile.firstName
      )
    ) {
      setCommonAlertTwoButton({ open: true, msg: t("profile_incomplete") });
      return;
    }

    if (type === "add") {
      if (!providers) {
        setCommonAlert({ open: true, msg: t("provider_not_found") });
        return;
      }
      setModalType("add");
      setModalOpen(true);
    } else if (type === "withdraw") {
      if (parseFloat(profile.walletBalance) <= 0) {
        setCommonAlert({ open: true, msg: t("wallet_bal_low") });
        return;
      }
      setModalType("withdraw");
      setModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setAmount(0);
  };

  const gotoOperation = () => {
    navigate("/profile");
    setCommonAlert({ open: false, msg: "" });
  };

  const cancelOperationTowButton = () => {
    setCommonAlertTwoButton({ open: false, msg: "" });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (amount <= 0) {
      setCommonAlert({ open: true, msg: t("valid_amount") });
      return;
    }

    if (modalType === "withdraw") {
      if (parseFloat(profile?.walletBalance) < amount) {
        setCommonAlert({ open: true, msg: t("valid_amount") });
        return;
      }
      dispatch(withdrawBalance(profile, amount));
      handleCloseModal();
    }
    // Add to wallet is handled by the form submission
  };

  const createOrderId = () => {
    const c = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const reference = [...Array(4)]
      .map((_) => c[~~(Math.random() * c.length)])
      .join("");
    return "wallet-" + auth.profile.uid + "-" + reference;
  };

  const percentGrowth = (Math.random() * 2 - 1) * 10;

  return (
    <Box className={classes.pageContainer}>
      <Typography variant="h4" className={classes.pageTitle}>
        {t("my_wallet_title")}
      </Typography>

      <Stack direction={"row"} alignItems="flex-start" gap={4}>
        {/* Wallet Balance Card */}

        <Box className={classes.walletCard}>
          {/* Background wave pattern */}
          <Box className={classes.waveBg} />

          <div>
            <Typography variant="body2" className={classes.walletBalanceLabel}>
              {t("wallet_balance")}
            </Typography>

            <Typography variant="h4" className={classes.walletBalanceValue}>
              {settings.symbol}{" "}
              {parseFloat(profile?.walletBalance || 0).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Typography>
          </div>

          {/* {percentGrowth < 0 ? (
            <Typography
              variant="caption"
              className={classes.trendIndicator}
              color={"#D32F2F"}
            >
              <TrendingDownIcon
                className={classes.trendIcon}
                color={"#D32F2F"}
              />
              {`${percentGrowth.toFixed(2)}% ${t("down")} ${t(
                "from_yesterday"
              )}`}
            </Typography>
          ) : (
            <Typography
              variant="caption"
              className={classes.trendIndicator}
              color={"#4F913C"}
            >
              <TrendingUp className={classes.trendIcon} color={"#4F913C"} />
              {`${percentGrowth.toFixed(2)}% ${t("up")} ${t("from_yesterday")}`}
            </Typography>
          )} */}
        </Box>

        {/* Add to Wallet Button */}
        <Stack
          gap={4}
          direction={"row"}
          sx={{ display: "flex", alignItems: "center" }}
        >
          <Button
            onClick={() => handleOpenModal("add")}
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ ...getStyles(role).addToWallet }}
          >
            {t("add_to_wallet_title")}
          </Button>

          {role === "driver" && (
            <Button
              onClick={() => handleOpenModal("withdraw")}
              variant="contained"
              sx={{ ...getStyles(role).withdrawFunds }}
              endIcon={<ArrowForward />}
            >
              {t("withdraw_cash")}
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Transaction History Table */}
      <Box className={classes.transactionContainer}>
        <TransactionTable data={data} isRTL={isRTL} settings={settings} />
      </Box>

      {/* Add to Wallet Modal */}
      <AddToWallet
        getStyles={getStyles}
        role={role}
        setSelectedProviderIndex={setSelectedProviderIndex}
        providers={providers}
        setSelectedProvider={setSelectedProvider}
        classes={classes}
        createOrderId={createOrderId}
        settings={settings}
        setAmount={setAmount}
        setCommonAlert={setCommonAlert}
        profile={profile}
        icons={icons}
        handleSubmit={handleSubmit}
        amount={amount}
        rootRef={rootRef}
        modalType={modalType}
        modalOpen={modalOpen}
        selectedProvider={selectedProvider}
        handleCloseModal={handleCloseModal}
        selectedProviderIndex={selectedProviderIndex}
      />

      <AlertDialog
        open={commonAlert.open}
        onClose={() => setCommonAlert({ open: false, msg: "" })}
      >
        {commonAlert.msg}
      </AlertDialog>

      <AlertDialogTwoButton
        open={commonAlertTwoButton.open}
        onClose={cancelOperationTowButton}
        onGoto={gotoOperation}
      >
        {commonAlertTwoButton.msg}
      </AlertDialogTwoButton>
    </Box>
  );
};

export default UserWallet;
